/**
 * Peaks for a timeline, from the original recording, however long it is.
 *
 * The recorder writes a fragmented MP4: an initialization segment (ftyp +
 * moov) and then a moof + mdat pair every couple of seconds. A run of pairs
 * decodes on its own with the initialization segment put in front of it,
 * so the file is read as a stream and decoded about ten seconds at a time,
 * at 8 kHz mono, into a fixed number of buckets. An hour costs a few
 * megabytes of memory rather than a gigabyte, and the first peaks are on
 * screen before the download is done.
 *
 * A file that is not fragmented (an older upload) is decoded whole if it
 * is short, and skipped if it is not.
 */

export interface WaveformProgress {
  /** Peak per bucket, 0 to 1, across the whole duration. */
  peaks: Float32Array;
  /** Seconds covered so far. */
  coveredSec: number;
  done: boolean;
}

const BUCKETS = 2400;
const DECODE_RATE = 8000;
const WHOLE_FILE_LIMIT_SEC = 20 * 60;
const PAIRS_PER_BATCH = 5;

function atomType(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(
    bytes[offset + 4],
    bytes[offset + 5],
    bytes[offset + 6],
    bytes[offset + 7],
  );
}

function atomSize(bytes: Uint8Array, offset: number): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 16);
  const size = view.getUint32(0);
  return size === 1 ? Number(view.getBigUint64(8)) : size;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.byteLength, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.byteLength;
  }
  return out;
}

export async function loadWaveform(
  url: string,
  durationSec: number,
  onProgress: (progress: WaveformProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const peaks = new Float32Array(BUCKETS);
  const secondsPerBucket = durationSec / BUCKETS;
  const context = new OfflineAudioContext(1, 1, DECODE_RATE);

  const fold = (audio: AudioBuffer, startSec: number) => {
    const data = audio.getChannelData(0);
    const rate = audio.sampleRate;
    const first = Math.max(0, Math.floor(startSec / secondsPerBucket));
    const last = Math.min(BUCKETS - 1, Math.floor((startSec + audio.duration) / secondsPerBucket));
    for (let b = first; b <= last; b += 1) {
      const from = Math.max(0, Math.floor((b * secondsPerBucket - startSec) * rate));
      const to = Math.min(data.length, Math.ceil(((b + 1) * secondsPerBucket - startSec) * rate));
      let peak = peaks[b];
      for (let i = from; i < to; i += 2) {
        const v = Math.abs(data[i]);
        if (v > peak) peak = v;
      }
      peaks[b] = peak;
    }
  };

  // Low priority: the original is being fetched for the player at the same
  // time, and the picture matters more than the waveform behind it.
  const response = await fetch(url, { signal, priority: 'low' } as RequestInit);
  if (!response.ok || !response.body) throw new Error(`waveform fetch ${response.status}`);
  const reader = response.body.getReader();

  let buffered = new Uint8Array(0);
  const head: Uint8Array[] = [];
  let init: Uint8Array | null = null;
  let fragmented = false;
  let pendingMoof: Uint8Array | null = null;
  let batch: Uint8Array[] = [];
  let cursorSec = 0;
  const whole: Uint8Array[] = [];

  const flush = async () => {
    if (!init || batch.length === 0) return;
    const bytes = concat([init, ...batch]);
    batch = [];
    try {
      const audio = await context.decodeAudioData(
        bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength),
      );
      fold(audio, cursorSec);
      cursorSec += audio.duration;
      onProgress({ peaks, coveredSec: cursorSec, done: false });
    } catch {
      // A run that would not decode leaves a gap; the next one carries on
      // from where the recorder's clock says it should.
      cursorSec += 2 * PAIRS_PER_BATCH;
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (value) buffered = concat([buffered, value]);

    let offset = 0;
    while (buffered.byteLength - offset >= 16) {
      const size = atomSize(buffered, offset);
      if (size < 8 || buffered.byteLength - offset < size) break;
      const type = atomType(buffered, offset);
      const atom = buffered.slice(offset, offset + size);
      offset += size;

      if (!init) {
        head.push(atom);
        if (type === 'moov') init = concat(head);
        continue;
      }
      if (type === 'moof') {
        fragmented = true;
        pendingMoof = atom;
      } else if (type === 'mdat' && fragmented && pendingMoof) {
        batch.push(pendingMoof, atom);
        pendingMoof = null;
        if (batch.length >= PAIRS_PER_BATCH * 2) await flush();
      } else {
        whole.push(atom);
      }
    }
    buffered = buffered.slice(offset);

    if (done) break;
    if (init && !fragmented && durationSec > WHOLE_FILE_LIMIT_SEC && whole.length > 0) {
      // Not fragmented and long: a whole-file decode is a gigabyte. Skip.
      await reader.cancel();
      onProgress({ peaks, coveredSec: 0, done: true });
      return;
    }
  }

  if (fragmented) {
    await flush();
  } else if (init && durationSec <= WHOLE_FILE_LIMIT_SEC) {
    try {
      const bytes = concat([init, ...whole]);
      const audio = await context.decodeAudioData(
        bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength),
      );
      fold(audio, 0);
    } catch {
      // Undecodable; the timeline goes without.
    }
  }
  onProgress({ peaks, coveredSec: durationSec, done: true });
}
